/* eslint-disable react/jsx-no-bind */
import { Component } from 'react';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { LicensingRuleType } from '../../../shared/model-typings';
import type { LicensingPolicy } from '../../../typings/api';
import type { PolicyAction } from '../../../typings/common';
import { window } from '../../globals';
import LicenseModule from '../../redux/licenses';
import PolicyModule from '../../redux/policies';

import type { AsyncStatus, LicensingRule } from '../../types';
import { hasError, verify_subscription } from '../../utils';
import { ErrorAlert, SearchInput } from '../generic';
import Loader from '../generic/Loader';
import { customizeableLicensePoliciesFeature } from '../settings/billing/features/consts';
import EditPolicyCTA from './EditPolicyCTA';
import { RuleItem } from './rules/RuleItem';
import { RuleList } from './rules/RuleList';
import type { NewRule, RuleModalMode } from './rules/RuleModal';
import { RuleModal } from './rules/RuleModal';
import LicenseTextModal from './modals/LicenseTextModal';

type DisplayLicensePolicyPageProps = {
  params: {
    policyId: number;
  };
  fetchLicenses: (params: { onlyIds: 'true'; pageSize: number }) => Promise<void>;
  createPolicyRule(policyId: number, rule: NewRule): Promise<void>;
  deletePolicyRule(policyId: number, ruleId: number): Promise<void>;
  updatePolicyRule(policyId: number, editedRuleId: number, rule: Partial<LicensingRule>): Promise<void>;
  fetchPolicy: (policyId: number) => Promise<void>;
  updatePolicy(
    updates: { id: number } & Partial<{ title: string; description: string; defaultAction: PolicyAction }>,
  ): Promise<void>;
  policy: LicensingPolicy;
  status: AsyncStatus;
  licenses: LicenseIdOnly[];
  licenseStatus: AsyncStatus;
};

type LicenseIdOnly = {
  licenseId: string;
};

type DisplayLicensePolicyPageState = {
  showRuleModal: boolean;
  modalMode: Partial<RuleModalMode>;
  uncategorizedSearch: string;
  deniedSearch: string;
  flaggedSearch: string;
  approvedSearch: string;
  selectedLicenseId: string | null;
  selectedRuleId: number | null;
};

class DisplayLicensePolicyPage extends Component<DisplayLicensePolicyPageProps, DisplayLicensePolicyPageState> {
  constructor(props: DisplayLicensePolicyPageProps) {
    super(props);
    this.state = {
      showRuleModal: false,
      modalMode: { editType: 'CREATE', type: 'approved_license' },
      uncategorizedSearch: '',
      deniedSearch: '',
      flaggedSearch: '',
      approvedSearch: '',
      selectedLicenseId: null,
      selectedRuleId: null,
    };
  }

  componentDidMount() {
    this.props.fetchPolicy(this.props.params.policyId);
    if (this.props.licenses.length > 0) {
      return;
    }

    this.props.fetchLicenses({
      onlyIds: 'true',
      pageSize: 4999,
    });
  }

  private getPolicyRuleLicenseIds = (): Set<string> =>
    new Set(this.props.policy?.rules?.map((rule) => rule.licenseId) ?? []);

  private isLicenseInPolicy = (licenseId: string) =>
    this.getPolicyRuleLicenseIds().has(licenseId);

  private doesSearchMatchLicense = (search: string, licenseId: string) =>
    licenseId.toLowerCase().includes(search.toLowerCase());

  private filterLicensesBySearch = (licenses: LicenseIdOnly[], search: string) =>
    licenses.filter((l) => {
      return this.doesSearchMatchLicense(search, l.licenseId) && !this.isLicenseInPolicy(l.licenseId);
    });

  private createRule = (type: LicensingRuleType, defaultRule?: Partial<LicensingRule>) =>
    verify_subscription('premium')
      ? this.setState({ showRuleModal: true, modalMode: { type, editType: 'CREATE', rule: defaultRule } })
      : window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });

  private editRule = (ruleId: number) =>
    verify_subscription('premium')
      ? this.setState({
        showRuleModal: true,
        modalMode: {
          editType: 'EDIT',
          rule: this.props.policy.rules?.find((rule) => rule.ruleId === ruleId),
        },
      })
      : window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });

  private hideRuleModal = () => this.setState({ showRuleModal: false });

  private hideLicenseModal = () => this.setState({ selectedLicenseId: null, selectedRuleId: null });

  private showLicenseDetails = (licenseId: string | null, ruleId: number | null) => {
    this.setState({ selectedLicenseId: licenseId, selectedRuleId: ruleId });
  };

  private deleteRule = (ruleId: number) => {
    this.props.deletePolicyRule(this.props.policy.id, ruleId);
    this.hideLicenseModal();
  };

  private submit = (rule: NewRule, editedRuleId?: number) => {
    if (this.state.modalMode.editType === 'CREATE') {
      this.props.createPolicyRule(this.props.policy.id, rule);
    } else if (this.state.modalMode.editType === 'EDIT') {
      this.props.updatePolicyRule(this.props.policy.id, editedRuleId as number, rule);
    }
    this.hideRuleModal();
    this.hideLicenseModal();
  };

  render() {
    if (!this.props.policy) {
      return <Loader />;
    }
    return (
      <>
        <EditPolicyCTA policy={this.props.policy} status={this.props.status} />
        <div className="container-fluid margin-top">
          <Helmet title={this.props.status.loading ? 'Policy...' : `${this.props.policy.title} - Policies`} />
          {this.props.status.loading ? (
            <Loader />
          ) : (
            <div>
              {hasError(this.props.status) && <ErrorAlert status={this.props.status} />}
              <div className="row">
                {/* See: https://github.com/fossas/FOSSA/pull/2613 for comments on uncategorized licenses */}
                {/* Uncategorized Licenses */}
                <div className="col-md-3">
                  <div className="margin-bottom-xs">
                    <SearchInput
                      placeholder="Search uncategorized licenses..."
                      value={this.state.uncategorizedSearch}
                      onChange={(uncategorizedSearch) => this.setState({ uncategorizedSearch })}
                      label="Uncategorized Licenses"
                      size="small"
                    />
                  </div>
                  <RuleList
                    // Uncategorized should never be the default
                    isDefault={false}
                    policy={this.props.policy}
                    title="Uncategorized"
                    onLicenseClick={this.showLicenseDetails}
                  >
                    {this.props.licenseStatus.loading || !this.props.licenses ? (
                      <Loader />
                    ) : (
                      this.filterLicensesBySearch(this.props.licenses, this.state.uncategorizedSearch)
                        .sort((a, b) => a.licenseId.toLowerCase().localeCompare(b.licenseId.toLowerCase()))
                        .map((l) => (
                          <RuleItem
                            key={l.licenseId}
                            policy={this.props.policy}
                            rule={l}
                            onLicenseClick={this.showLicenseDetails}
                          />
                        ))
                    )}
                  </RuleList>
                </div>
                {/* Denied Licenses */}
                <div className="col-md-3">
                  <div className="margin-bottom-xs">
                    <SearchInput
                      placeholder="Search denied licenses..."
                      onChange={(deniedSearch) => this.setState({ deniedSearch })}
                      value={this.state.deniedSearch}
                      label="Denied Licenses"
                      size="small"
                    />
                  </div>
                  <RuleList
                    isDefault={this.props.policy.latestVersion?.defaultAction === 'DENY'}
                    onRuleAdd={(defaultRule) => this.createRule('denied_license', defaultRule)}
                    onRuleEdit={(ruleId) => this.editRule(ruleId)}
                    onRuleRemove={this.deleteRule}
                    onSetAsDefault={() => this.props.updatePolicy({ id: this.props.policy.id, defaultAction: 'DENY' })}
                    policy={this.props.policy}
                    search={this.state.deniedSearch}
                    title="Deny"
                    ruleType="denied_license"
                    bsStyle="danger"
                    icon="times"
                    onLicenseClick={this.showLicenseDetails}
                  />
                </div>
                {/* Flagged Licenses */}
                <div className="col-md-3">
                  <div className="margin-bottom-xs">
                    <SearchInput
                      placeholder="Search flagged licenses..."
                      onChange={(flaggedSearch) => this.setState({ flaggedSearch })}
                      value={this.state.flaggedSearch}
                      label="Flagged Licenses"
                      size="small"
                    />
                  </div>
                  <RuleList
                    isDefault={this.props.policy.latestVersion?.defaultAction === 'FLAG'}
                    onRuleAdd={(defaultRule) => this.createRule('flagged_license', defaultRule)}
                    onRuleEdit={(ruleId) => this.editRule(ruleId)}
                    onRuleRemove={this.deleteRule}
                    onSetAsDefault={() => this.props.updatePolicy({ id: this.props.policy.id, defaultAction: 'FLAG' })}
                    search={this.state.flaggedSearch}
                    title="Flag for Review"
                    bsStyle="warning"
                    ruleType="flagged_license"
                    policy={this.props.policy}
                    icon="flag"
                    onLicenseClick={this.showLicenseDetails}
                  />
                </div>
                {/* Approved Licenses */}
                <div className="col-md-3">
                  <div className="margin-bottom-xs">
                    <SearchInput
                      placeholder="Search approved licenses..."
                      onChange={(approvedSearch) => this.setState({ approvedSearch })}
                      value={this.state.approvedSearch}
                      label="Approved Licenses"
                      size="small"
                    />
                  </div>
                  <RuleList
                    isDefault={this.props.policy.latestVersion?.defaultAction === 'APPROVE'}
                    onRuleAdd={(defaultRule) => this.createRule('approved_license', defaultRule)}
                    onRuleEdit={(ruleId) => this.editRule(ruleId)}
                    onRuleRemove={this.deleteRule}
                    onSetAsDefault={() =>
                      this.props.updatePolicy({ id: this.props.policy.id, defaultAction: 'APPROVE' })}
                    search={this.state.approvedSearch}
                    title="Approve"
                    ruleType="approved_license"
                    bsStyle="success"
                    policy={this.props.policy}
                    icon="check"
                    onLicenseClick={this.showLicenseDetails}
                  />
                </div>
              </div>
            </div>
          )}
          <RuleModal
            show={this.state.showRuleModal}
            mode={this.state.modalMode as RuleModalMode}
            onClose={this.hideRuleModal}
            onSubmit={this.submit}
          />
          <LicenseTextModal
            policy={this.props.policy}
            licenseId={this.state.selectedLicenseId}
            ruleId={this.state.selectedRuleId}
            onClose={this.hideLicenseModal}
            onEdit={this.editRule}
            onAdd={(licenseId: string) => this.createRule('approved_license', { licenseId })}
            onDelete={this.deleteRule}
          />
        </div>
      </>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  // policies
  policy: PolicyModule.selectors.getCurrentPolicy,
  status: PolicyModule.selectors.getStatus,

  // licenses
  licenses: LicenseModule.selectors.getLicenseList,
  licenseStatus: LicenseModule.selectors.getStatus,
});

const mapDispatchToProps = {
  fetchLicenses: LicenseModule.actions.fetchAll,
  createPolicyRule: PolicyModule.actions.createPolicyRule,
  deletePolicyRule: PolicyModule.actions.deletePolicyRule,
  updatePolicyRule: PolicyModule.actions.updatePolicyRule,
  fetchPolicy: PolicyModule.actions.fetchOne,
  updatePolicy: PolicyModule.actions.update,
};

// @ts-expect-error -- the JSX vs. ReactNode conflict is a "bug" in the types for the connect function using DragDropContext + class based components
export default DragDropContext(HTML5Backend)(connect(mapStateToProps, mapDispatchToProps)(DisplayLicensePolicyPage));
